// AVISO: texto gerado como rascunho inicial para dar um ponto de partida.
// Precisa ser revisado por um advogado (LGPD) antes de valer como documento
// legal oficial. Razão Social, CNPJ e o contato do encarregado (DPO) vêm de
// /admin/configuracoes — preencher por lá.
import { createFileRoute } from "@tanstack/react-router";
import { formatPhone } from "@/lib/format";
import { useStoreSettings } from "@/lib/settings.functions";
import { requestOriginQuery } from "@/lib/seo.functions";

export const Route = createFileRoute("/privacidade")({
  loader: async ({ context }) => ({
    origin: await context.queryClient.ensureQueryData(requestOriginQuery()),
  }),
  head: ({ loaderData }) => {
    const canonicalUrl = loaderData?.origin ? `${loaderData.origin}/privacidade` : undefined;
    return {
      meta: [
        { title: "Política de Privacidade — Loja EG Elétrica" },
        {
          name: "description",
          content: "Como a EG Elétrica coleta, usa e protege seus dados pessoais.",
        },
        ...(canonicalUrl ? [{ property: "og:url", content: canonicalUrl }] : []),
      ],
      links: canonicalUrl ? [{ rel: "canonical", href: canonicalUrl }] : [],
    };
  },
  component: PrivacidadePage,
});

function PrivacidadePage() {
  const settings = useStoreSettings();

  return (
    <div className="mx-auto max-w-3xl px-4 py-12">
      <h1 className="font-display text-2xl font-bold text-eg-dark mb-2">Política de Privacidade</h1>
      <p className="mb-8 text-sm text-muted-foreground">Última atualização: 31 de julho de 2026.</p>

      <div className="space-y-6 text-sm leading-relaxed text-eg-dark">
        <section>
          <h2 className="mb-2 font-display font-bold">1. Introdução</h2>
          <p>
            Esta Política explica como{" "}
            <strong>
              {settings.razao_social ?? settings.nome_fantasia}
              {settings.razao_social ? ` — ${settings.nome_fantasia}` : ""}
            </strong>
            {settings.cnpj && (
              <>
                {" "}
                (CNPJ <strong>{settings.cnpj}</strong>)
              </>
            )}{" "}
            coleta, usa, armazena e protege seus dados pessoais, em conformidade com a Lei Geral de
            Proteção de Dados (Lei nº 13.709/2018 — LGPD).
          </p>
        </section>

        <section>
          <h2 className="mb-2 font-display font-bold">2. Quais dados coletamos</h2>
          <ul className="list-disc space-y-1 pl-5">
            <li>Dados de identificação e contato: nome, e-mail, telefone, CPF.</li>
            <li>Dados de entrega: endereço completo.</li>
            <li>
              Dados de conta: e-mail e senha (armazenada de forma criptografada), se você criar
              conta.
            </li>
            <li>Dados de navegação: páginas visitadas, para melhorar a experiência de compra.</li>
          </ul>
        </section>

        <section>
          <h2 className="mb-2 font-display font-bold">3. Para que usamos seus dados</h2>
          <ul className="list-disc space-y-1 pl-5">
            <li>Processar e entregar seus pedidos.</li>
            <li>Emitir nota fiscal e cumprir obrigações fiscais e legais.</li>
            <li>Comunicar sobre o status do seu pedido.</li>
            <li>Prevenir fraudes e garantir a segurança das compras.</li>
          </ul>
        </section>

        <section>
          <h2 className="mb-2 font-display font-bold">4. Com quem compartilhamos</h2>
          <p>Compartilhamos dados estritamente necessários com:</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <strong>Mercado Pago</strong>, para processar o pagamento (Pix e cartão de crédito).
            </li>
            <li>
              <strong>Tiny/Olist</strong> (sistema de gestão), para emissão de nota fiscal e
              controle de estoque.
            </li>
            <li>
              <strong>Supabase</strong>, provedor de hospedagem do banco de dados da loja.
            </li>
          </ul>
          <p className="mt-2">Não vendemos seus dados a terceiros.</p>
        </section>

        <section>
          <h2 className="mb-2 font-display font-bold">5. Armazenamento e segurança</h2>
          <p>
            Seus dados são armazenados em servidores com controles de acesso e criptografia em
            trânsito. Mantemos os dados pelo tempo necessário para cumprir as finalidades descritas
            nesta Política e obrigações legais (fiscais, por exemplo).
          </p>
        </section>

        <section>
          <h2 className="mb-2 font-display font-bold">6. Seus direitos (Art. 18 da LGPD)</h2>
          <p>Você pode, a qualquer momento, solicitar:</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>Confirmação da existência de tratamento de dados.</li>
            <li>Acesso, correção ou atualização dos seus dados.</li>
            <li>Anonimização, bloqueio ou eliminação de dados desnecessários.</li>
            <li>Portabilidade dos dados a outro fornecedor.</li>
            <li>Revogação do consentimento e exclusão da sua conta.</li>
          </ul>
        </section>

        <section>
          <h2 className="mb-2 font-display font-bold">7. Cookies</h2>
          <p>
            Usamos cookies essenciais ao funcionamento do site (ex.: manter o carrinho de compras e
            a sessão de login), que não exigem consentimento por serem estritamente necessários.
            Cookies de análise de audiência (Google Analytics) só são ativados depois que você
            aceita o aviso exibido na primeira visita — você pode recusar sem prejuízo ao uso da
            loja.
          </p>
        </section>

        <section>
          <h2 className="mb-2 font-display font-bold">8. Encarregado de Dados (DPO)</h2>
          <p>
            Para exercer seus direitos ou tirar dúvidas sobre esta Política, entre em contato
            {settings.email_dpo && (
              <>
                {" "}
                pelo e-mail <strong>{settings.email_dpo}</strong> ou
              </>
            )}{" "}
            pelo WhatsApp {formatPhone(settings.whatsapp_number.slice(-11))}.
          </p>
        </section>

        <section>
          <h2 className="mb-2 font-display font-bold">9. Alterações desta política</h2>
          <p>
            Podemos atualizar esta Política periodicamente. A versão vigente é sempre a publicada
            nesta página, com a data de atualização indicada no topo.
          </p>
        </section>
      </div>
    </div>
  );
}
